/**
 * Shared horizontal bounds for chat transcript content.
 *
 * These are symmetrical left/right bounds even when a screenshot or review
 * calls out only one edge:
 *
 * - Surface bounds are the default outer rail for parent surfaces: message/tool
 *   cards, media grids, file viewers, suggestion controls, and the composer shell.
 * - Reading bounds are the inner limits for prose and minimal inline activity:
 *   assistant text, compact status/tool icons, and small text labels.
 *
 * These rails are defaults, not hard maximums. A tool whose content benefits
 * from more room may deliberately break out to the available workspace or
 * viewport width. Keep its prose and compact chrome Reading-bound, declare the
 * breakout explicitly, cap it to a safe width, and keep ancestor overflow open.
 * `show_files` media comparisons are the canonical example.
 *
 * Rule: non-breakout renderer roots use the surface bounds and must not add a
 * reading inset to their outer wrapper. Apply reading-bound inset classes only
 * to prose, compact inline rows, or labels inside a renderer. Insetting the
 * renderer root shifts cards and media away from the composer and breaks the
 * shared surface bounds.
 *
 * Split-pane chat warning: default/non-breakout tool rows must not derive their
 * intrinsic width from long text or viewport breakpoints. In the artifact side
 * chat, those rows can force the pane wider instead of shrinking. Long status
 * text should be omitted or aggressively truncated, and media grids should use
 * an explicit grid template. An intentional breakout is different: its renderer
 * explicitly owns and safely caps the wider rail after the parent allows it.
 */
export const CHAT_MESSAGE_SURFACE_BOUNDS_WIDTH_CLASS =
  'max-w-[min(100%,540px)] min-w-0 sm:max-w-[min(100%,440px)] md:max-w-[min(100%,500px)] lg:max-w-[min(100%,600px)] xl:max-w-[min(100%,700px)]';

export const CHAT_MESSAGE_SURFACE_BOUNDS_CONTAINER_CLASS =
  'mx-auto w-full min-w-0 overflow-visible';

export const CHAT_MESSAGE_RENDERER_SURFACE_CLASS = `w-full max-w-full min-w-0 overflow-visible ${CHAT_MESSAGE_SURFACE_BOUNDS_WIDTH_CLASS}`;

// Keep layout invalidation scoped to individual transcript rows without using
// content-visibility, which can collapse estimated scroll height for chat rows.
export const CHAT_MESSAGE_ROW_PERFORMANCE_CLASS = '[contain:layout_style]';

// User turns separate the preceding exchange from the next assistant reply.
// The hover-only action rail still occupies height below the bubble, so the
// outer rhythm needs more space before the turn and no additional bottom pad.
export const USER_MESSAGE_VERTICAL_RHYTHM_CLASS = 'mt-10 pb-0';

// Reading-bound insets. The left and right values define the inner reading
// area relative to the outer surface bounds; use them for prose, minimal status
// rows/icons, and compact labels—not for cards, media, or the composer shell.
export const CHAT_MESSAGE_READING_BOUNDS_INSET_X_CLASS = 'px-3.5';
export const CHAT_MESSAGE_READING_BOUNDS_INSET_LEFT_CLASS = 'pl-3.5';
export const CHAT_MESSAGE_READING_BOUNDS_INSET_RIGHT_CLASS = 'pr-3.5';
export const CHAT_MESSAGE_READING_BOUNDS_READABLE_INSET_CLASS = 'pr-2.5 pl-3.5';

// Reading measure applies to prose-shaped children inside the Reading bounds.
// Keep it off renderer roots so tables, code, media, and Work artifacts can
// continue to use the full Surface bounds.
export const CHAT_MESSAGE_READING_MEASURE_CLASS = 'max-w-[68ch]';

/** The rail supplied by a renderer's immediate chat container. */
export type ChatMessageContentRail = 'reading' | 'surface';

// Exact outer-row contract for compact chat chrome that sits above wider
// media. The surrounding 700px Surface contains a symmetrical px-3.5 Reading
// inset, so the live Reading rail is 672px. Keep this calculation here instead
// of duplicating a local width in Work and file-viewer headers. Logical
// properties keep the centered rail direction-neutral, while inline styles
// prevent breakout ancestors from stretching either measured outer row. The
// nearest chat container supplies the responsive Surface width; subtracting
// both 14px insets keeps this rail aligned with the surrounding message copy.
export const CHAT_MESSAGE_READING_CONTENT_RAIL_STYLE = {
  inlineSize: 'min(100%, calc(100cqw - 1.75rem))',
  maxInlineSize: 'calc(700px - 1.75rem)',
  marginInline: 'auto',
} as const;

/**
 * Compatibility aliases for existing callers. New alignment work should use
 * the explicit SURFACE_BOUNDS / READING_BOUNDS names above.
 */
export const CHAT_MESSAGE_WIDTH_CLASS = CHAT_MESSAGE_SURFACE_BOUNDS_WIDTH_CLASS;
export const CHAT_MESSAGE_CONTAINER_CLASS =
  CHAT_MESSAGE_SURFACE_BOUNDS_CONTAINER_CLASS;
export const CHAT_MESSAGE_TEXT_INSET_X_CLASS =
  CHAT_MESSAGE_READING_BOUNDS_INSET_X_CLASS;
export const CHAT_MESSAGE_TEXT_INSET_LEFT_CLASS =
  CHAT_MESSAGE_READING_BOUNDS_INSET_LEFT_CLASS;
export const CHAT_MESSAGE_TEXT_INSET_RIGHT_CLASS =
  CHAT_MESSAGE_READING_BOUNDS_INSET_RIGHT_CLASS;
export const CHAT_MESSAGE_TEXT_READABLE_INSET_CLASS =
  CHAT_MESSAGE_READING_BOUNDS_READABLE_INSET_CLASS;

// Single grouped tools render without a disclosure header, but their row must
// keep the same vertical footprint as the header used once tools form a
// cluster. Share the token so those two render paths cannot drift apart.
export const CHAT_TOOL_ACTIVITY_ROW_Y_CLASS = 'py-1.5';

// Before-content headers use a slightly tighter inset than prose. Share these
// tokens with expanded work headers so their label does not shift on toggle.
export const CHAT_MESSAGE_BEFORE_CONTENT_INSET_X_CLASS = 'px-3';
export const CHAT_MESSAGE_BEFORE_CONTENT_JOINED_Y_CLASS = 'pt-2.5 pb-1';

// Single source of truth for visible chat card radii. User message bubbles,
// assistant response cards, and org work-block cards should all use this token
// so sticky/pinned states do not drift into a different shape.
export const CHAT_MESSAGE_SURFACE_RADIUS_CLASS = 'rounded-[12px]';
